import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Clock, ShieldCheck, ChevronLeft, ImageOff, Lock } from "lucide-react";
import AdCard from "../components/site/AdCard";
import ContactLock from "../components/site/ContactLock";
import { ShareButtons, SellerCard } from "../components/site/AdExtras";
import { DirectOwnerBadge } from "../components/site/PropertyHighlight";
import SoldStamp from "../components/site/SoldStamp";
import { PROPERTY_TYPES, formatArea } from "../lib/categories";
import { http, imgUrl, formatPrice, timeAgo, useMeta } from "../lib/api";
import { useAuth } from "../lib/auth";

function Gallery({ photos, title, sold }) {
  const [i, setI] = useState(0);
  if (!photos.length) return <div className="relative aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden flex items-center justify-center text-slate-400"><ImageOff className="w-12 h-12" />{sold && <SoldStamp big />}</div>;
  return (
    <div>
      <div className="relative aspect-[4/3] bg-black rounded-2xl overflow-hidden">
        <img src={imgUrl(photos[i])} alt={title} className="w-full h-full object-contain" data-testid="ad-detail-main-photo" />
        {sold && <SoldStamp big />}
      </div>
      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {photos.map((p, j) => (
            <button key={p} onClick={() => setI(j)} className={`w-20 h-16 shrink-0 rounded-lg overflow-hidden border-2 ${i === j ? "border-[var(--orange)]" : "border-transparent"}`} data-testid={`ad-detail-thumb-${j}`}>
              <img src={imgUrl(p)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function useContact(ad) {
  const { user } = useAuth();
  const [mobile, setMobile] = useState(null);
  const active = user && user.plan_state === "active" && !ad.sold;
  useEffect(() => {
    setMobile(null);
    if (active) http.get(`/ads/${ad.id}/contact`).then((r) => setMobile(r.data.mobile)).catch(() => {});
  }, [ad.id, active]);
  return mobile;
}

function SoldOutBox() {
  return (
    <div className="bg-white rounded-2xl border-2 border-[var(--sell)] p-5 text-center space-y-2" data-testid="sold-out-box">
      <p className="font-display font-extrabold text-3xl text-[var(--sell)] tracking-widest" data-testid="sold-out-label">SOLD OUT</p>
      <p className="font-semibold text-[var(--navy)]">ही वस्तू / प्रॉपर्टी विकली गेली आहे</p>
      <p className="text-xs text-slate-500">विक्रेत्याशी संपर्क बंद आहे. अशाच इतर जाहिराती खाली पहा.</p>
      <Link to="/buy" className="inline-flex h-11 px-5 items-center rounded-xl bg-[var(--buy)] text-white font-semibold" data-testid="sold-out-browse">इतर जाहिराती पहा</Link>
    </div>
  );
}

function ContactBox({ ad, mobile }) {
  if (ad.sold) return <SoldOutBox />;
  const msg = encodeURIComponent(`नमस्कार, मला 99Bazaar Maharashtra वरील तुमच्या जाहिरातीत रस आहे: ${ad.title} (${formatPrice(ad.price)})`);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
      <p className="text-sm text-slate-500">Seller Contact · विक्रेता (थेट संपर्क, No Broker)</p>
      {mobile ? (
        <>
          <p className="font-display font-bold text-xl text-[var(--navy)]" data-testid="ad-detail-mobile">+91 {mobile.slice(0, 5)} {mobile.slice(5)}</p>
          <a href={`tel:+91${mobile}`} className="h-12 rounded-xl bg-[var(--buy)] hover:bg-[#0d47a1] text-white font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="call-seller-button"><Phone className="w-5 h-5" /> Call करा</a>
          <a href={`https://wa.me/91${mobile}?text=${msg}`} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="whatsapp-seller-button"><MessageCircle className="w-5 h-5" /> WhatsApp करा</a>
        </>
      ) : (
        <>
          <p className="font-display font-bold text-xl text-[var(--navy)] tracking-wider select-none blur-[5px]" aria-hidden="true" data-testid="ad-detail-mobile-hidden">+91 98765 43210</p>
          <ContactLock />
        </>
      )}
      <div className="rounded-xl bg-orange-50 border border-orange-100 p-3 text-xs text-slate-700 space-y-1">
        <p className="font-semibold flex items-center gap-1 text-[var(--navy)]"><ShieldCheck className="w-4 h-4 text-[var(--orange)]" /> Safety Tips</p>
        <p>वस्तू प्रत्यक्ष पाहूनच पैसे द्या. Never pay in advance or share OTP.</p>
      </div>
    </div>
  );
}

export default function AdDetail() {
  const { id } = useParams();
  const [ad, setAd] = useState(null);
  const [related, setRelated] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setAd(null); setMissing(false); window.scrollTo(0, 0);
    http.get(`/ads/${id}`).then((r) => setAd(r.data)).catch(() => setMissing(true));
    http.get(`/ads/${id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated([]));
  }, [id]);

  if (missing) return <div className="max-w-xl mx-auto py-20 text-center" data-testid="ad-not-found"><p className="text-xl font-semibold">जाहिरात सापडली नाही · Ad not found</p><Link to="/buy" className="text-[var(--buy)] mt-3 inline-block">← Browse ads</Link></div>;
  if (!ad) return <div className="max-w-7xl mx-auto px-4 py-10"><div className="aspect-[16/9] bg-slate-200 rounded-2xl animate-pulse" /></div>;

  return <AdView ad={ad} related={related} />;
}

function AdView({ ad, related }) {
  const { user } = useAuth();
  const freeMode = useMeta()?.free_mode;
  const mobile = useContact(ad);
  const place = [ad.place, ad.taluka && ad.taluka !== ad.place ? ad.taluka : null, ad.location].filter(Boolean).join(", ");
  return (
    <div className="max-w-7xl mx-auto px-4 pt-6 pb-24 lg:pb-8 md:pt-8">
      <Link to="/buy" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[var(--navy)]" data-testid="ad-detail-back-link"><ChevronLeft className="w-4 h-4" /> सर्व जाहिराती</Link>
      <div className="mt-4 grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-6">
          <Gallery photos={ad.photos} title={ad.title} sold={ad.sold} />
          {ad.sold && <div className="rounded-2xl bg-[var(--sell)] text-white px-5 py-4 font-display font-bold text-lg md:text-xl text-center" data-testid="ad-sold-message">SOLD · ही वस्तू / प्रॉपर्टी विकली गेली आहे</div>}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6">
            <span className="text-xs font-semibold bg-[#E3EEFC] text-[var(--buy)] px-2 py-1 rounded-full" data-testid="ad-detail-category">{ad.category}</span>
            {ad.condition && <span className={`ml-2 text-xs font-bold text-white px-2 py-1 rounded-full ${ad.condition === "old" ? "bg-[var(--orange)]" : "bg-[var(--buy)]"}`} data-testid="ad-detail-condition">{ad.condition === "old" ? "जुना बाजार" : "नवीन बाजार"}</span>}
            {ad.category === "Property" && ad.direct_owner && !ad.sold && <div className="mt-3"><DirectOwnerBadge big /></div>}
            <p className="mt-3 font-display font-extrabold text-3xl text-[var(--navy)]" data-testid="ad-detail-price">{formatPrice(ad.price)}</p>
            <h1 className="mt-1 text-xl md:text-2xl font-semibold text-slate-800" data-testid="ad-detail-title">{ad.title}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1" data-testid="ad-detail-location"><MapPin className="w-4 h-4" /> {place}, Maharashtra</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {timeAgo(ad.created_at)}</span>
            </div>
            <hr className="my-5" />
            {ad.category === "Property" && ad.property_type && (
              <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ad-detail-property">
                {[["प्रकार", PROPERTY_TYPES[ad.property_type]?.mr], ["एरिया", formatArea(ad)], ["गाव", ad.place], ["7/12", ad.has_712 === true ? "आहे ✓" : ad.has_712 === false ? "नाही" : "—"]].map(([k, v]) => (
                  <div key={k} className="rounded-xl bg-green-50 border border-green-100 p-3"><p className="text-xs text-slate-500">{k}</p><p className="font-semibold text-[var(--navy)]">{v || "—"}</p></div>
                ))}
              </div>
            )}
            <ShareButtons ad={ad} />
            <hr className="my-5" />
            <p className="font-semibold text-[var(--navy)] mb-2">वर्णन · Description</p>
            <p className="whitespace-pre-line text-slate-700 leading-relaxed" data-testid="ad-detail-description">{ad.description}</p>
          </div>
        </div>
        <aside className="lg:sticky lg:top-24 h-fit space-y-4"><ContactBox ad={ad} mobile={mobile} /><SellerCard ownerId={ad.owner_id} /></aside>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 p-3 grid grid-cols-2 gap-3" data-testid="mobile-contact-bar">
        {ad.sold ? (
          <div className="col-span-2 h-12 rounded-xl bg-slate-200 text-[var(--sell)] font-display font-extrabold tracking-widest flex items-center justify-center" data-testid="mobile-sold-out">SOLD OUT</div>
        ) : mobile ? (
          <>
            <a href={`tel:+91${mobile}`} className="h-12 rounded-xl bg-[var(--buy)] text-white font-semibold flex items-center justify-center gap-2" data-testid="mobile-call-button"><Phone className="w-5 h-5" /> Call</a>
            <a href={`https://wa.me/91${mobile}`} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2" data-testid="mobile-whatsapp-button"><MessageCircle className="w-5 h-5" /> WhatsApp</a>
          </>
        ) : (
          <Link to={user ? "/plans" : `/login?next=/ad/${ad.id}`} className="col-span-2 h-12 rounded-xl bg-[var(--orange)] text-white font-semibold flex items-center justify-center gap-2" data-testid="mobile-unlock-button"><Lock className="w-5 h-5" /> {freeMode ? "संपर्क पाहण्यासाठी Login करा - मोफत" : user && user.plan_state === "expired" ? "प्लॅन संपला - पुन्हा सुरू करा" : "संपर्क पाहण्यासाठी ₹99 भरा"}</Link>
        )}
      </div>
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display font-bold text-2xl text-[var(--navy)]">Similar Ads · <span className="text-[var(--orange)]">अशाच जाहिराती</span></h2>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">{related.map((r) => <AdCard key={r.id} ad={r} />)}</div>
        </section>
      )}
    </div>
  );
}
